/**
 * The theme shows the sticky add to cart only once the real button has scrolled past the top of
 * the viewport. On a phone that button starts below the fold, so nothing is offered until the
 * shopper scrolls. This flips the rule for small screens: the bar is there on landing and steps
 * aside only while the real button is on screen.
 *
 * It sets a class of its own rather than the theme's `is-visible`, which the theme keeps
 * updating for the desktop behaviour underneath.
 */
(() => {
  const MOBILE_QUERY = 'screen and (max-width: 699px)';
  const SELECTOR = 'product-sticky-add-to-cart[data-mobile-early]';

  class EarlyStickyVisibility {
    constructor(element) {
      this.element = element;
      this.buttonInView = false;
      this.footerInView = false;
      this.mediaQuery = window.matchMedia(MOBILE_QUERY);
      this.observer = new IntersectionObserver(this.onObserve.bind(this));

      this.mediaQuery.addEventListener('change', this.onMediaChange.bind(this));
      this.onMediaChange();
    }

    get addToCartButton() {
      const formId = this.element.getAttribute('main-product-form');
      const buyButtons = document.querySelector(`buy-buttons[form="${formId}"]`);

      // The button itself, so the bar steps aside for it rather than for the whole block.
      return buyButtons?.querySelector('button[type="submit"]') ?? buyButtons;
    }

    get footer() {
      return document.querySelector('.shopify-section--footer');
    }

    onMediaChange() {
      this.observer.disconnect();

      if (!this.mediaQuery.matches) {
        this.element.classList.remove('is-visible-early');
        return;
      }

      // Nothing to step aside for, so leave the bar to the theme.
      const addToCartButton = this.addToCartButton;
      if (!addToCartButton) return;

      this.buttonInView = false;
      this.footerInView = false;
      this.observer.observe(addToCartButton);

      if (this.footer) this.observer.observe(this.footer);

      this.update();
    }

    onObserve(entries) {
      entries.forEach((entry) => {
        if (entry.target === this.footer) {
          this.footerInView = entry.isIntersecting && entry.boundingClientRect.top < window.innerHeight;
        } else {
          this.buttonInView = entry.isIntersecting;
        }
      });

      this.update();
    }

    update() {
      this.element.classList.toggle('is-visible-early', !this.buttonInView && !this.footerInView);
    }
  }

  const setup = (root = document) => {
    root.querySelectorAll?.(SELECTOR).forEach((element) => {
      if (element.earlyStickyVisibility) return;
      element.earlyStickyVisibility = new EarlyStickyVisibility(element);
    });
  };

  setup();
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
})();
